import { useState, useEffect, useRef } from "@wordpress/element";
import { useConversation } from "@11labs/react";
import AudioWaveform from "./AudioWaveform";

const FloatingButton = ({
  position,
  color,
  title,
  customClass = "",
  customStyle = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voiceState, setVoiceState] = useState("initial");
  const [showTerms, setShowTerms] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(() => {
    return localStorage.getItem("voice_agent_terms_agreed") === "true";
  });
  const [messages, setMessages] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({});
  const [isMobileView, setIsMobileView] = useState(false);
  const [isMobileCompact, setIsMobileCompact] = useState(true);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaFailed, setRecaptchaFailed] = useState(false);
  const [localRecaptchaSettings, setLocalRecaptchaSettings] = useState(null);

  const chatRef = useRef(null);
  const startTimeRef = useRef(null);
  const conversationIdRef = useRef(null);
  const recaptchaRef = useRef(null);

  const buttonSettings = gcVoiceChat?.settings?.buttonSettings || {};
  const recaptchaSettings = localRecaptchaSettings ||
    gcVoiceChat?.settings?.recaptcha || {
      enabled: false,
      siteKey: "",
    };

  const gravityFormsSettings = gcVoiceChat?.settings?.gravityForms || {
    enabled: false,
    formId: "",
  };

  const contactForm7Settings = gcVoiceChat?.settings?.contactForm7 || {
    enabled: false,
    formId: "",
  };

  const defaultText = buttonSettings.defaultText || "TEST AI AGENT";
  const showDefaultLeftIcon = buttonSettings.showDefaultLeftIcon || false;
  const defaultLeftIconUrl = buttonSettings.defaultLeftIconUrl || "";
  const showDefaultRightIcon = buttonSettings.showDefaultRightIcon || false;
  const defaultRightIconUrl = buttonSettings.defaultRightIconUrl || "";
  const showDefaultWaveform = buttonSettings.showDefaultWaveform !== false;
  const defaultWaveformText = buttonSettings.defaultWaveformText || "24/7";
  const defaultBgColor = buttonSettings.defaultBgColor || "#F9BD15";
  const defaultHoverColor = buttonSettings.defaultHoverColor || "#F98215";
  const showDefaultWaveformIcon =
    buttonSettings.showDefaultWaveformIcon !== false;

  const connectingText = buttonSettings.connectingText || "CONNECTING...";
  const showConnectingLeftIcon = buttonSettings.showConnectingLeftIcon || false;
  const connectingLeftIconUrl = buttonSettings.connectingLeftIconUrl || "";
  const showConnectingRightIcon =
    buttonSettings.showConnectingRightIcon || false;
  const connectingRightIconUrl = buttonSettings.connectingRightIconUrl || "";
  const showConnectingWaveform =
    buttonSettings.showConnectingWaveform !== false;
  const connectingWaveformText = buttonSettings.connectingWaveformText || "";
  const connectingBgColor = buttonSettings.connectingBgColor || "#F9BD15";
  const connectingHoverColor = buttonSettings.connectingHoverColor || "#F98215";
  const showConnectingWaveformIcon =
    buttonSettings.showConnectingWaveformIcon !== false;

  const listeningText = buttonSettings.listeningText || "LISTENING";
  const showListeningLeftIcon = buttonSettings.showListeningLeftIcon || false;
  const listeningLeftIconUrl = buttonSettings.listeningLeftIconUrl || "";
  const showListeningRightIcon = buttonSettings.showListeningRightIcon || false;
  const listeningRightIconUrl = buttonSettings.listeningRightIconUrl || "";
  const showListeningWaveform = buttonSettings.showListeningWaveform !== false;
  const listeningWaveformText = buttonSettings.listeningWaveformText || "";
  const listeningBgColor = buttonSettings.listeningBgColor || "#F9BD15";
  const listeningHoverColor = buttonSettings.listeningHoverColor || "#F98215";
  const showListeningWaveformIcon =
    buttonSettings.showListeningWaveformIcon !== false;

  const speakingText = buttonSettings.speakingText || "TALK TO INTERRUPT";
  const showSpeakingLeftIcon = buttonSettings.showSpeakingLeftIcon || false;
  const speakingLeftIconUrl = buttonSettings.speakingLeftIconUrl || "";
  const showSpeakingRightIcon = buttonSettings.showSpeakingRightIcon || false;
  const speakingRightIconUrl = buttonSettings.speakingRightIconUrl || "";
  const showSpeakingWaveform = buttonSettings.showSpeakingWaveform !== false;
  const speakingWaveformText = buttonSettings.speakingWaveformText || "";
  const speakingBgColor = buttonSettings.speakingBgColor || "#F9BD15";
  const speakingHoverColor = buttonSettings.speakingHoverColor || "#F98215";
  const showSpeakingWaveformIcon =
    buttonSettings.showSpeakingWaveformIcon !== false;

  const recaptchaFailedText =
    buttonSettings.recaptchaFailedText || "CAPTCHA FAILED";

  const enableMobileMode = buttonSettings.enableMobileMode || false;
  const mobileText = buttonSettings.mobileText || "CHAT";
  const mobileIconUrl = buttonSettings.mobileIconUrl || "";
  const mobileBgColor = buttonSettings.mobileBgColor || "#000000";
  const mobileTextColor = buttonSettings.mobileTextColor || "#FFFFFF";
  const mobileHoverColor = buttonSettings.mobileHoverColor || "#333333";
  const mobileWidth = buttonSettings.mobileWidth || 80;
  const mobileHeight = buttonSettings.mobileHeight || 40;

  const buttonTextColor = buttonSettings.buttonTextColor || "#000000";
  const buttonHoverTextColor = buttonSettings.buttonHoverTextColor || "#000000";
  const buttonBorderRadius = buttonSettings.buttonBorderRadius || "9999";
  const waveformColor = buttonSettings.waveformColor || "#FFFFFF";
  const waveformCircleColor = buttonSettings.waveformCircleColor || "#000000";
  const waveformWidth = buttonSettings.waveformWidth || 1;

  const cancelTextColor = buttonSettings.cancelTextColor || "#666666";
  const cancelBgColor = buttonSettings.cancelBgColor || "#FFFFFF";
  const cancelHoverColor = buttonSettings.cancelHoverColor || "#F5F5F5";
  const agreeTextColor = buttonSettings.agreeTextColor || "#FFFFFF";
  const agreeBgColor = buttonSettings.agreeBgColor || "#000000";
  const agreeHoverColor = buttonSettings.agreeHoverColor || "#333333";

  const enableTerms =
    buttonSettings.enableTerms !== undefined
      ? buttonSettings.enableTerms
      : true;
  const termsTitle = buttonSettings.termsTitle || "Terms and conditions";
  const termsContent =
    buttonSettings.termsContent ||
    'By clicking "Agree," and each time I interact with this AI agent, I consent to the recording, storage, and sharing of my communications with third-party service providers, and as described in the Privacy Policy. If you do not wish to have your conversations recorded, please refrain from using this service.';
  const termsPositionBottom = buttonSettings.termsPositionBottom || "0";
  const termsPositionRight = buttonSettings.termsPositionRight || "0";

  const getStateBgColor = () => {
    switch (voiceState) {
      case "connecting":
        return connectingBgColor;
      case "listening":
        return listeningBgColor;
      case "speaking":
        return speakingBgColor;
      default:
        return defaultBgColor;
    }
  };

  const getStateHoverColor = () => {
    switch (voiceState) {
      case "connecting":
        return connectingHoverColor;
      case "listening":
        return listeningHoverColor;
      case "speaking":
        return speakingHoverColor;
      default:
        return defaultHoverColor;
    }
  };

  const buttonStyles = {
    "--button-bg-color": getStateBgColor(),
    "--button-hover-color": getStateHoverColor(),
    "--button-text-color": buttonTextColor,
    "--button-hover-text-color": buttonHoverTextColor,
    "--button-border-radius": `${buttonBorderRadius}px`,
    ...(customStyle ? JSON.parse(customStyle) : {}),
  };

  const mobileButtonStyles = {
    "--mobile-bg-color": mobileBgColor,
    "--mobile-hover-color": mobileHoverColor,
    "--mobile-text-color": mobileTextColor,
    "--button-border-radius": `${buttonBorderRadius}px`,
    width: isMobileCompact ? `${mobileWidth}px` : "auto",
    height: `${mobileHeight}px`,
  };

  const { status, isSpeaking, startSession, endSession } = useConversation({
    onConnect: () => {
      addMessage("system", "Connected! You can start speaking now.");
      startTimeRef.current = new Date();
    },
    onDisconnect: () => {
      setIsStarted(false);
      setVoiceState("initial");

      if (startTimeRef.current) {
        const endTime = new Date();
        const duration = Math.round((endTime - startTimeRef.current) / 1000);
        const minutes = Math.floor(duration / 60);
        const seconds = duration % 60;
        const recordingTime = `${minutes}:${seconds
          .toString()
          .padStart(2, "0")}`;

        setFormData((prevData) => ({
          ...prevData,
          recordingTime: recordingTime,
        }));

        if (conversationIdRef.current) {
          setTimeout(() => {
            fetchConversationData(conversationIdRef.current);
          }, 2000);
        } else {
          checkAndSubmitToForms();
        }
      }
    },
    onMessage: (message) => {
      if (
        message.type === "speech_partial" ||
        message.type === "speech_final"
      ) {
        handleUserMessage(message);
        setVoiceState("listening");
      } else if (
        message.type === "text_reply" ||
        message.type === "text_reply_chunk"
      ) {
        handleAgentMessage(message);
        setVoiceState("speaking");
      }
    },
    onError: (error) => {
      console.error("Conversation error:", error);
      setError("An error occurred. Please try again.");
      setIsLoading(false);
      setVoiceState("initial");
    },
  });

  useEffect(() => {
    const fetchSettings = async () => {
      const recaptchaEnabled =
        window.wpRecaptchaSettings?.enabled === true ||
        (recaptchaSettings && recaptchaSettings.enabled === true) ||
        typeof window.wpcf7_recaptcha !== "undefined";

      if (
        !recaptchaEnabled &&
        !window.wpRecaptchaSettings &&
        !localRecaptchaSettings &&
        (!recaptchaSettings || !recaptchaSettings.enabled)
      ) {
        try {
          const shouldCheckRecaptcha =
            window.gcVoiceChat?.settings?.checkRecaptcha === true;

          if (!shouldCheckRecaptcha) {
            return;
          }

          const ajaxUrl =
            window.ajaxurl ||
            (window.gcVoiceChat && window.gcVoiceChat.ajaxUrl) ||
            "/wp-admin/admin-ajax.php";

          const response = await fetch(ajaxUrl, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({
              action: "gc_ai_voice_agent_get_recaptcha_settings",
            }),
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              setLocalRecaptchaSettings(result.data);
            }
          }
        } catch (error) {
          console.error("Error fetching reCAPTCHA settings:", error);
        }
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        if (
          !recaptchaSettings ||
          (recaptchaSettings.enabled === false &&
            document.querySelector(
              '[name="gc_ai_voice_agent_enable_recaptcha"][value="1"]:checked'
            ))
        ) {
          const response = await fetch(gcVoiceChat.ajaxUrl, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: new URLSearchParams({
              action: "gc_ai_voice_agent_get_recaptcha_settings",
              nonce: gcVoiceChat.nonce,
            }),
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success) {
              setLocalRecaptchaSettings(result.data);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching admin reCAPTCHA settings:", error);
      }
    };

    fetchAdminSettings();
  }, []);

  useEffect(() => {
    if (window.wpRecaptchaSettings && window.wpRecaptchaSettings.enabled) {
      setLocalRecaptchaSettings(window.wpRecaptchaSettings);
    }

    const currentSettings = localRecaptchaSettings || recaptchaSettings;

    if (currentSettings && currentSettings.enabled && currentSettings.siteKey) {
      const existingScript = document.querySelector(`script[src*="recaptcha"]`);

      if (!existingScript) {
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${currentSettings.siteKey}`;
        script.async = true;
        script.defer = true;
        script.id = "recaptcha-script";
        script.onload = () => {
          setTimeout(() => {
            if (window.grecaptcha && window.grecaptcha.ready) {
              window.grecaptcha.ready(() => {
                setRecaptchaLoaded(true);
              });
            } else {
              setRecaptchaFailed(true);
            }
          }, 1000);
        };
        script.onerror = () => {
          setRecaptchaFailed(true);
        };
        document.head.appendChild(script);
      } else if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          setRecaptchaLoaded(true);
        });
      } else {
        const checkRecaptcha = setInterval(() => {
          if (window.grecaptcha && window.grecaptcha.ready) {
            window.grecaptcha.ready(() => {
              setRecaptchaLoaded(true);
              clearInterval(checkRecaptcha);
            });
          }
        }, 500);

        setTimeout(() => {
          clearInterval(checkRecaptcha);
          if (!recaptchaLoaded) {
            setRecaptchaFailed(true);
          }
        }, 10000);
      }
    }
  }, [localRecaptchaSettings, recaptchaSettings]);

  useEffect(() => {
    const container = document.getElementById(
      "gc-ai-voice-agent-floating-button"
    );
    const displayMode = container
      ? container.getAttribute("data-display-mode")
      : gcVoiceChat.settings.displayMode;
    const shortcodeUsed =
      document.querySelector('[data-shortcode="true"]') !== null;

    if (displayMode === "all_pages") {
      setIsVisible(true);
    } else if (displayMode === "shortcode_only") {
      if (shortcodeUsed) {
        setIsVisible(true);
      }
    } else if (displayMode === "element_click") {
      const triggerSelector = gcVoiceChat.settings.triggerElement;

      if (triggerSelector) {
        setTimeout(() => {
          const triggerElements = document.querySelectorAll(triggerSelector);

          if (triggerElements.length > 0) {
            triggerElements.forEach((element) => {
              element.addEventListener("click", (e) => {
                e.preventDefault();
                setIsVisible(true);
              });
            });
          }
        }, 500);
      }
    }

    const checkMobileView = () => {
      if (enableMobileMode && window.innerWidth < 768) {
        setIsMobileView(true);
      } else {
        setIsMobileView(false);
        setIsMobileCompact(false);
      }
    };

    checkMobileView();

    window.addEventListener("resize", checkMobileView);

    return () => {
      window.removeEventListener("resize", checkMobileView);
    };
  }, []);

  // FIXED: verifyRecaptcha function to properly handle different scenarios
  const verifyRecaptcha = async () => {
    try {
      // Check if we need to use reCAPTCHA at all
      const useCaptcha =
        window.wpRecaptchaSettings?.enabled === true ||
        (recaptchaSettings && recaptchaSettings.enabled === true) ||
        (localRecaptchaSettings && localRecaptchaSettings.enabled === true) ||
        typeof window.wpcf7_recaptcha !== "undefined";

      // If no reCAPTCHA is needed, return true immediately - don't proceed further
      if (!useCaptcha) {
        console.log("reCAPTCHA not required, proceeding");
        return true;
      }

      // Try CF7 reCAPTCHA integration first
      if (typeof window.wpcf7_recaptcha !== "undefined") {
        if (
          !window.wpcf7_recaptcha.sitekey ||
          window.wpcf7_recaptcha.sitekey.length < 5
        ) {
          console.error("CF7 reCAPTCHA key is missing or invalid");
          setRecaptchaFailed(true);
          return false;
        }

        try {
          const siteKey = window.wpcf7_recaptcha.sitekey;

          if (!window.grecaptcha || !window.grecaptcha.ready) {
            console.error("grecaptcha not loaded");
            setRecaptchaFailed(true);
            return false;
          }

          const token = await window.grecaptcha.execute(siteKey, {
            action: "submit",
          });

          if (!token || token.length < 10) {
            console.error("Invalid token from grecaptcha.execute");
            setRecaptchaFailed(true);
            return false;
          }

          return true;
        } catch (error) {
          console.error("Error executing CF7 reCAPTCHA", error);
          setRecaptchaFailed(true);
          return false;
        }
      }

      // Fall back to plugin settings
      const settings =
        window.wpRecaptchaSettings ||
        localRecaptchaSettings ||
        recaptchaSettings;

      if (!settings || !settings.enabled) {
        return true;
      }

      if (!settings.siteKey || settings.siteKey.length < 5) {
        console.error("reCAPTCHA site key is missing or invalid");
        setRecaptchaFailed(true);
        return false;
      }

      if (!window.grecaptcha || !window.grecaptcha.ready) {
        try {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/api.js?render=${settings.siteKey}`;
            script.async = true;
            script.defer = true;
            script.id = "recaptcha-script-ondemand";
            script.onload = () => {
              setTimeout(() => {
                if (window.grecaptcha && window.grecaptcha.ready) {
                  window.grecaptcha.ready(resolve);
                } else {
                  reject("Failed to load reCAPTCHA");
                }
              }, 1000);
            };
            script.onerror = reject;
            document.head.appendChild(script);
          });
        } catch (error) {
          console.error("Failed to load reCAPTCHA script", error);
          setRecaptchaFailed(true);
          return false;
        }
      }

      try {
        const token = await window.grecaptcha.execute(settings.siteKey, {
          action: "voice_agent_button",
        });

        if (!token || token.length < 10) {
          console.error("Invalid token from grecaptcha.execute");
          setRecaptchaFailed(true);
          return false;
        }

        try {
          const formData = new FormData();
          formData.append("action", "gc_ai_voice_agent_verify_recaptcha");
          formData.append("nonce", gcVoiceChat.nonce);
          formData.append("token", token);

          const params = new URLSearchParams();
          for (const pair of formData.entries()) {
            params.append(pair[0], pair[1]);
          }

          const response = await fetch(gcVoiceChat.ajaxUrl, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: params,
          });

          if (!response.ok) {
            console.error("Server error during reCAPTCHA verification");
            setRecaptchaFailed(true);
            return false;
          }

          const result = await response.json();

          if (result.success) {
            setRecaptchaFailed(false);
            return true;
          } else {
            console.error("reCAPTCHA verification failed on server");
            setRecaptchaFailed(true);
            return false;
          }
        } catch (serverError) {
          console.error(
            "Server error during reCAPTCHA verification",
            serverError
          );
          setRecaptchaFailed(true);
          return false;
        }
      } catch (recaptchaError) {
        console.error("reCAPTCHA execution error", recaptchaError);
        setRecaptchaFailed(true);
        return false;
      }
    } catch (error) {
      console.error("General reCAPTCHA error", error);
      setRecaptchaFailed(true);
      return false;
    }
  };

  const checkAndSubmitToForms = () => {
    const dataToSubmit = {
      ...formData,
      transcript: createTranscript(messages),
    };

    if (gravityFormsSettings.enabled && gravityFormsSettings.formId) {
      submitToGravityForms(dataToSubmit);
    }

    if (contactForm7Settings.enabled && contactForm7Settings.formId) {
      submitToContactForm7(dataToSubmit);
    }
  };

  const fetchConversationData = async (convId) => {
    try {
      if (!convId) {
        return;
      }

      const formData = new FormData();
      formData.append("action", "gc_ai_voice_agent_get_conversation_data");
      formData.append("nonce", gcVoiceChat.nonce);
      formData.append("conversation_id", convId);

      const params = new URLSearchParams();
      for (const pair of formData.entries()) {
        params.append(pair[0], pair[1]);
      }

      const response = await fetch(gcVoiceChat.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setFormData((prevData) => ({
          ...prevData,
          ...result.data,
        }));

        const dataToSubmit = {
          ...result.data,
          transcript: createTranscript(messages),
        };

        if (gravityFormsSettings.enabled && gravityFormsSettings.formId) {
          submitToGravityForms(dataToSubmit);
        }

        if (contactForm7Settings.enabled && contactForm7Settings.formId) {
          submitToContactForm7(dataToSubmit);
        }
      } else {
        checkAndSubmitToForms();
      }
    } catch (error) {
      checkAndSubmitToForms();
    }
  };

  const submitToGravityForms = async (data) => {
    try {
      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });

      formData.append("action", "gc_ai_voice_agent_submit_to_gravity");
      formData.append("nonce", gcVoiceChat.nonce);

      if (conversationIdRef.current) {
        formData.append("conversation_id", conversationIdRef.current);
      }

      const params = new URLSearchParams();
      for (const pair of formData.entries()) {
        params.append(pair[0], pair[1]);
      }

      const response = await fetch(gcVoiceChat.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });

      await response.json();
    } catch (error) {}
  };

  const submitToContactForm7 = async (data) => {
    try {
      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });

      formData.append("action", "gc_ai_voice_agent_submit_to_cf7");
      formData.append("nonce", gcVoiceChat.nonce);

      if (conversationIdRef.current) {
        formData.append("conversation_id", conversationIdRef.current);
      }

      const params = new URLSearchParams();
      for (const pair of formData.entries()) {
        params.append(pair[0], pair[1]);
      }

      const response = await fetch(gcVoiceChat.ajaxUrl, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });

      await response.json();
    } catch (error) {}
  };

  const createTranscript = (messages) => {
    return messages
      .map((msg) => {
        const sender =
          msg.sender === "user"
            ? "User"
            : msg.sender === "agent"
            ? "AI Agent"
            : "System";
        return `${sender}: ${msg.text}`;
      })
      .join("\n\n");
  };

  const handleUserMessage = (message) => {
    if (message.type === "speech_final") {
      addMessage("user", message.text);
    }
  };

  const handleAgentMessage = (message) => {
    if (message.type === "text_reply") {
      addMessage("agent", message.text);
    } else if (message.type === "text_reply_chunk") {
      setMessages((prevMessages) => {
        const newMessages = [...prevMessages];
        const lastAgentMessageIndex = [...newMessages]
          .reverse()
          .findIndex((m) => m.sender === "agent");

        if (lastAgentMessageIndex >= 0) {
          const actualIndex = newMessages.length - 1 - lastAgentMessageIndex;
          newMessages[actualIndex].text += message.text;
        } else {
          newMessages.push({ sender: "agent", text: message.text });
        }

        return newMessages;
      });
    }
  };

  const addMessage = (sender, text) => {
    setMessages((prevMessages) => [...prevMessages, { sender, text }]);
  };

  useEffect(() => {
    if (isSpeaking) {
      setVoiceState("speaking");
    } else if (isStarted && !isSpeaking) {
      setVoiceState("listening");
    }
  }, [isSpeaking, isStarted]);

  const getPositionClass = () => {
    switch (position) {
      case "top-left":
        return "gc-ai-voice-agent-top-left";
      case "top-right":
        return "gc-ai-voice-agent-top-right";
      case "bottom-left":
        return "gc-ai-voice-agent-bottom-left";
      case "bottom-right":
      default:
        return "gc-ai-voice-agent-bottom-right";
    }
  };

  // FIXED: initiateConversation to properly handle captcha results
  const initiateConversation = async () => {
    setIsLoading(true);
    setError(null);
    setVoiceState("connecting");

    try {
      // Check if we need to verify reCAPTCHA
      const captchaResult = await verifyRecaptcha();

      if (!captchaResult) {
        setError("reCAPTCHA verification failed. Please try again.");
        setIsLoading(false);
        setVoiceState("initial");
        setRecaptchaFailed(true);
        return;
      }
    } catch (recaptchaError) {
      console.error("reCAPTCHA verification error", recaptchaError);
      setError("reCAPTCHA verification failed. Please try again.");
      setIsLoading(false);
      setVoiceState("initial");
      setRecaptchaFailed(true);
      return;
    }

    try {
      const agentId = gcVoiceChat.settings.agentId;
      if (!agentId) {
        throw new Error(
          "Agent ID is not configured. Please check the plugin settings."
        );
      }

      try {
        const newConversationId = await startSession({
          agentId: agentId,
        });

        if (!newConversationId) {
          throw new Error("No conversation ID returned from startSession");
        }

        conversationIdRef.current = newConversationId;
        setIsStarted(true);
        setVoiceState("listening");
        return;
      } catch (directError) {
        console.error("Direct session start failed", directError);
        throw directError; // Rethrow to be caught by the outer catch
      }
    } catch (err) {
      console.error("Conversation initiation error:", err);
      setError(
        err.message || "Failed to start conversation. Please try again."
      );
      setVoiceState("initial");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMobileClick = () => {
    if (isMobileCompact && !isStarted) {
      setIsMobileCompact(false);
    } else if (isStarted) {
      endConversation();
    } else {
      startConversation();
    }
  };

  const startConversation = async () => {
    if (enableTerms && !termsAgreed) {
      setShowTerms(true);
      return;
    }

    setFormData({});
    // Don't reset recaptchaFailed here, it should persist until verification succeeds
    conversationIdRef.current = null;
    setMessages([]);

    await initiateConversation();
  };

  const endConversation = async () => {
    await endSession();
    setIsStarted(false);
    addMessage("system", "Conversation ended.");
    setVoiceState("initial");

    if (isMobileView) {
      setIsMobileCompact(true);
    }
  };

  const handleAgreeTerms = () => {
    localStorage.setItem("voice_agent_terms_agreed", "true");
    setTermsAgreed(true);
    setShowTerms(false);

    initiateConversation();
  };

  const handleCancelTerms = () => {
    setShowTerms(false);

    if (isMobileView) {
      setIsMobileCompact(true);
    }
  };

  const getButtonText = () => {
    if (recaptchaFailed) return recaptchaFailedText;
    if (voiceState === "connecting") return connectingText;
    if (voiceState === "listening") return listeningText;
    if (voiceState === "speaking") return speakingText;
    return defaultText;
  };

  const getCurrentStateSettings = () => {
    if (recaptchaFailed) {
      return {
        showLeftIcon: showDefaultLeftIcon,
        leftIconUrl: defaultLeftIconUrl,
        showRightIcon: showDefaultRightIcon,
        rightIconUrl: defaultRightIconUrl,
        showWaveform: showDefaultWaveform,
        showWaveformIcon: buttonSettings.defaultWaveformUseIcon || false,
        waveformText: defaultWaveformText,
        waveformIconUrl: buttonSettings.defaultWaveformIconUrl || "",
        buttonText: recaptchaFailedText,
      };
    }

    switch (voiceState) {
      case "connecting":
        return {
          showLeftIcon: showConnectingLeftIcon,
          leftIconUrl: connectingLeftIconUrl,
          showRightIcon: showConnectingRightIcon,
          rightIconUrl: connectingRightIconUrl,
          showWaveform: showConnectingWaveform,
          showWaveformIcon: buttonSettings.connectingWaveformUseIcon || false,
          waveformText: connectingWaveformText,
          waveformIconUrl: buttonSettings.connectingWaveformIconUrl || "",
          buttonText: connectingText,
        };
      case "listening":
        return {
          showLeftIcon: showListeningLeftIcon,
          leftIconUrl: listeningLeftIconUrl,
          showRightIcon: showListeningRightIcon,
          rightIconUrl: listeningRightIconUrl,
          showWaveform: showListeningWaveform,
          showWaveformIcon: buttonSettings.listeningWaveformUseIcon || false,
          waveformText: listeningWaveformText,
          waveformIconUrl: buttonSettings.listeningWaveformIconUrl || "",
          buttonText: listeningText,
        };
      case "speaking":
        return {
          showLeftIcon: showSpeakingLeftIcon,
          leftIconUrl: speakingLeftIconUrl,
          showRightIcon: showSpeakingRightIcon,
          rightIconUrl: speakingRightIconUrl,
          showWaveform: showSpeakingWaveform,
          showWaveformIcon: buttonSettings.speakingWaveformUseIcon || false,
          waveformText: speakingWaveformText,
          waveformIconUrl: buttonSettings.speakingWaveformIconUrl || "",
          buttonText: speakingText,
        };
      default:
        return {
          showLeftIcon: showDefaultLeftIcon,
          leftIconUrl: defaultLeftIconUrl,
          showRightIcon: showDefaultRightIcon,
          rightIconUrl: defaultRightIconUrl,
          showWaveform: showDefaultWaveform,
          showWaveformIcon: buttonSettings.defaultWaveformUseIcon || false,
          waveformText: defaultWaveformText,
          waveformIconUrl: buttonSettings.defaultWaveformIconUrl || "",
          buttonText: defaultText,
        };
    }
  };

  const {
    showLeftIcon,
    leftIconUrl,
    showRightIcon,
    rightIconUrl,
    showWaveform,
    showWaveformIcon,
    waveformText,
    waveformIconUrl,
    buttonText,
  } = getCurrentStateSettings();

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`${getPositionClass()} va-z-[9999]`}>
      <style>
        {`
                .voice-agent-button {
                    background-color: var(--button-bg-color);
                    color: var(--button-text-color);
                    border-radius: var(--button-border-radius);
                }
                .voice-agent-button:hover:not(:disabled) {
                    background-color: var(--button-hover-color);
                    color: var(--button-hover-text-color);
                }
                .mobile-voice-agent-button {
                    background-color: var(--mobile-bg-color);
                    color: var(--mobile-text-color);
                    border-radius: var(--button-border-radius);
                }
                .mobile-voice-agent-button:hover:not(:disabled) {
                    background-color: var(--mobile-hover-color);
                }
                .va-terms-cancel-button {
                    color: ${cancelTextColor};
                    background-color: ${cancelBgColor};
                }
                .va-terms-cancel-button:hover {
                    background-color: ${cancelHoverColor};
                }
                .va-terms-agree-button {
                    color: ${agreeTextColor};
                    background-color: ${agreeBgColor};
                }
                .va-terms-agree-button:hover {
                    background-color: ${agreeHoverColor};
                }
                `}
      </style>

      {showTerms && (
        <div
          className="gc-ai-voice-agent-terms va-absolute va-p-5 va-bg-white va-rounded-[4px] va-shadow-[0px_0px_20px_0px_rgba(0,0,0,0.25)] va-z-[10000] va-mx-auto lg:va-max-w-[400px] lg:va-min-w-[400px] va-flex va-flex-col va-gap-y-[0.875rem]"
          style={{ bottom: termsPositionBottom, right: termsPositionRight }}
        >
          <h4 className="va-m-0 va-text-lg va-font-bold va-text-black">
            {termsTitle}
          </h4>
          <div
            className="va-m-0 va-text-sm va-text-black va-leading-normal"
            dangerouslySetInnerHTML={{ __html: termsContent }}
          ></div>
          <div className="va-flex va-justify-end va-gap-2">
            <button
              type="button"
              className="va-inline-flex va-items-center va-justify-center va-border-none va-rounded-[4px] va-py-[0.938rem] va-px-[2.188rem] va-text-white va-text-base va-font-bold va-cursor-pointer va-transition-all va-terms-agree-button"
              onClick={handleAgreeTerms}
            >
              <div className="va-whitespace-nowrap va-overflow-hidden va-text-ellipsis">
                Agree
              </div>
            </button>
            <button
              type="button"
              className="va-inline-flex va-items-center va-justify-center va-rounded-[4px] va-py-[0.938rem] va-px-[2.188rem] va-bg-transparent va-text-black va-text-base va-font-bold va-cursor-pointer va-transition-all hover:va-bg-gray-50 va-terms-cancel-button"
              onClick={handleCancelTerms}
            >
              <div className="va-whitespace-nowrap va-overflow-hidden va-text-ellipsis">
                Cancel
              </div>
            </button>
          </div>
        </div>
      )}

      <div
        className="va-shadow-[0px_0px_20px_0px_rgba(0,0,0,0.25)]"
        style={{ borderRadius: `${buttonBorderRadius}px` }}
      >
        {isMobileView && isMobileCompact ? (
          <button
            onClick={handleMobileClick}
            className="va-flex va-py-[0.625rem] va-px-4 va-justify-center va-items-center
                      va-gap-2 va-self-stretch va-cursor-pointer va-font-bold
                      va-uppercase va-transition va-ease-in-out va-duration-150
                      mobile-voice-agent-button"
            style={mobileButtonStyles}
            disabled={isLoading}
          >
            <div className="va-flex va-items-center va-justify-center va-gap-[0.25rem]">
              <span className="va-text-xs">
                {recaptchaFailed ? recaptchaFailedText : mobileText}
              </span>
              {mobileIconUrl ? (
                <img
                  src={mobileIconUrl}
                  alt="Mobile Icon"
                  className="va-w-5 va-h-5"
                />
              ) : null}
            </div>
          </button>
        ) : (
          <button
            onClick={
              isMobileView
                ? isStarted
                  ? endConversation
                  : startConversation
                : isStarted
                ? endConversation
                : startConversation
            }
            className="va-flex va-h-14 va-w-full va-py-4 va-px-8 va-justify-center
                    va-items-center va-gap-2 va-self-stretch va-cursor-pointer
                    va-font-bold va-uppercase va-transition va-ease-in-out va-duration-150
                    voice-agent-button"
            style={buttonStyles}
            disabled={isLoading}
          >
            <div className="va-flex va-items-center va-justify-center va-gap-4">
              <div
                className={`va-flex va-items-center va-justify-center ${
                  showWaveform ? "va-w-8 va-h-8" : ""
                }`}
              >
                {showWaveform ? (
                  <AudioWaveform
                    isSpeaking={voiceState === "speaking"}
                    size={32}
                    waveformColor={waveformColor}
                    circleColor={waveformCircleColor}
                    waveformWidth={waveformWidth}
                  />
                ) : showWaveformIcon && waveformIconUrl ? (
                  <img
                    src={waveformIconUrl}
                    alt="Waveform Icon"
                    className="va-max-w-full va-max-h-full"
                  />
                ) : (
                  <span className="va-text-center waveformText">
                    {waveformText}
                  </span>
                )}
              </div>

              {showLeftIcon && leftIconUrl && (
                <div className="va-w-8 va-h-8 va-flex va-items-center va-justify-center leftIcon">
                  <img
                    src={leftIconUrl}
                    alt="Left Icon"
                    className="va-max-w-full va-max-h-full"
                  />
                </div>
              )}

              <span>{getButtonText()}</span>

              {showRightIcon && rightIconUrl && (
                <div className="va-w-8 va-h-8 va-flex va-items-center va-justify-center rightIcon">
                  <img
                    src={rightIconUrl}
                    alt="Right Icon"
                    className="va-max-w-full va-max-h-full "
                  />
                </div>
              )}
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

export default FloatingButton;
